import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthContext } from '../contexts/AuthContext';
import { getCached, setCached, shouldRevalidate } from '../utils/sessionCache';
import {
  UserStorageData,
  getStorageLimitBytes,
  getStorageLimitGB,
  getStorageUsagePercent,
  isStorageNearLimit,
  isStorageOverLimit,
  canUpload,
  bytesToGB,
  canUseAddons,
} from '../lib/storageQuota';

export interface StorageQuotaState {
  loading: boolean;
  error: string | null;
  usedBytes: number;
  usedGB: number;
  limitBytes: number;
  limitGB: number;
  usagePercent: number;
  isNearLimit: boolean;
  isOverLimit: boolean;
  canUploadFile: (fileSizeBytes: number) => boolean;
  canUseAddons: boolean;
  userData: UserStorageData | null;
}

export function useStorageQuota(): StorageQuotaState {
  const { user } = useAuthContext();
  const [userData, setUserData] = useState<UserStorageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setUserData(null);
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, 'users', user.uid);
    let previousTier: string | undefined = undefined;
    let pollInterval: NodeJS.Timeout | null = null;
    
    async function fetchUserData() {
      try {
        const cacheKey = `user_storage_${user.uid}`;
        const cached = getCached<any>(cacheKey);
        
        // Use cache if available and recent
        if (cached && !shouldRevalidate(cacheKey)) {
          const plan = cached.subscriptionTier || cached.plan || 'free';
          
          // Calculate total addon storage from storageAddons array
          const storageAddons = cached.storageAddons || [];
          const extraStorageGB = Array.isArray(storageAddons) 
            ? storageAddons.reduce((sum: number, addon: any) => sum + (addon.storageGB || 0) * (addon.quantity || 1), 0)
            : 0;
          
          const addons = { 
            extraStorageGB, 
            active: extraStorageGB > 0 
          };
          
          setUserData({
            plan,
            trialEndsAt: cached.trialEndsAt,
            storageUsedBytes: cached.storageUsedBytes || 0,
            addons,
          });
          setLoading(false);
          return;
        }
        
        // Fetch from Firestore
        const docSnapshot = await getDoc(userDocRef);
        
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const plan = data.subscriptionTier || data.plan || 'free';
          
          // Calculate total addon storage from storageAddons array
          const storageAddons = data.storageAddons || [];
          const extraStorageGB = Array.isArray(storageAddons) 
            ? storageAddons.reduce((sum: number, addon: any) => sum + (addon.storageGB || 0) * (addon.quantity || 1), 0)
            : 0;
          
          const addons = { 
            extraStorageGB, 
            active: extraStorageGB > 0 
          };
          
          // Detect subscription tier change and reload page
          if (previousTier !== undefined && previousTier !== plan) {
            console.log('[useStorageQuota] Subscription tier changed! Reloading page...');
            setTimeout(() => {
              window.location.reload();
            }, 500);
            return;
          }
          previousTier = plan;
          
          setUserData({
            plan,
            trialEndsAt: data.trialEndsAt,
            storageUsedBytes: data.storageUsedBytes || 0,
            addons,
          });
          setCached(cacheKey, data);
          setError(null);
        } else {
          setUserData({
            plan: 'free',
            storageUsedBytes: 0,
            addons: { extraStorageGB: 0, active: false },
          });
        }
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching storage quota:', err);
        setError(err.message);
        setLoading(false);
      }
    }

    fetchUserData();

    return () => {
      if (pollInterval) clearTimeout(pollInterval);
    };
  }, [user]);

  if (!userData) {
    return {
      loading,
      error,
      usedBytes: 0,
      usedGB: 0,
      limitBytes: 0,
      limitGB: 0,
      usagePercent: 0,
      isNearLimit: false,
      isOverLimit: false,
      canUploadFile: () => false,
      canUseAddons: false,
      userData: null,
    };
  }

  const limitBytes = getStorageLimitBytes(userData);
  const limitGB = getStorageLimitGB(userData);
  const usedBytes = userData.storageUsedBytes || 0;
  const usedGB = bytesToGB(usedBytes);
  const usagePercent = getStorageUsagePercent(userData);
  const nearLimit = isStorageNearLimit(userData);
  const overLimit = isStorageOverLimit(userData);
  const addonsAllowed = canUseAddons(userData.plan);

  return {
    loading,
    error,
    usedBytes,
    usedGB,
    limitBytes,
    limitGB,
    usagePercent,
    isNearLimit: nearLimit,
    isOverLimit: overLimit,
    canUploadFile: (fileSizeBytes: number) => canUpload(userData, fileSizeBytes),
    canUseAddons: addonsAllowed,
    userData,
  };
}
